import React, { useState } from "react";
import { MdSync, MdDeleteForever, MdCheckCircle, MdAdd, MdInventory2, MdImage, MdViewModule, MdVisibilityOff, MdCheck } from "react-icons/md";
import ShowimagesLoading from "./showImagesLoading";
import { convertCJVariantsToFilterProduct, deleteFilterProduct, updateCJVariantSwatchStyle } from "../auth/apiProduct";
import { elakayAutoText } from "./dataElakay";
import "./adfilterProduct.css";
import "./cjVariantsSyncPanel.css";
import "./DropshippingPasswordConfirmationModal.css";
const { CJSyncActionModal } = require("./sync/CJSyncActionModal");
const { PASSWORD_CONFIRMATION_ACTIONS, requestElakayPasswordConfirmation } = require("./sync/passwordConfirmation");
const { clearCJConfirmation, isCJConfirmationError } = require("./sync/CJPasswordConfirmationSession");
const { elakay_API } = require("../../config");

// CJ Dropshipping variants (left) vs. variants already synced into the
// Elakay product (right, tagged filter.__cjVariantId by
// syncCJVariantsToFilterProducts/convertCJVariantToFilterProduct server-side)
// -- "Sync All" converts every remaining CJ variant at once, the "+" button on
// each left row converts just that one, the trash button on each right row
// removes just that one filterProduct entry (never touches manually-added
// variants, which have no __cjVariantId).
const CJVariantsSyncPanel = ({ cjVariants = [], syncedFilterProducts = [], token, productsID, onSynced, variantSwatchStyle = "square" }) => {
  const [pendingId, setPendingId] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [updatingStyle, setUpdatingStyle] = useState(false);

  // Collects the Elakay password inline and passes it directly to the API
  // call (same pattern as CJSyncModal's finalizeCJProductSynchronization),
  // instead of relying on the global window.fetch confirmation interceptor
  // for this panel's actions.
  const [passwordPrompt, setPasswordPrompt] = useState(null); // { run: (password) => Promise<data> }
  const [passwordError, setPasswordError] = useState("");
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);

  const openPasswordPrompt = (run, config = {}) => {
    setPasswordPrompt({ run, ...config });
    setPasswordError("");
  };

  const closePasswordPrompt = () => {
    if (isSubmittingPassword) return;
    setPasswordPrompt(null);
    setPasswordError("");
  };

  const submitPasswordPrompt = async ({ confirmationToken }) => {
    if (!passwordPrompt || !confirmationToken || isSubmittingPassword) return;
    setIsSubmittingPassword(true);
    setPasswordError("");
    try {
      const data = await passwordPrompt.run(confirmationToken);
      if (data?.error) {
        // Unlike the CJDropshippingContext api client, these calls use a raw
        // fetch (apiProduct.js) with no shared onConfirmationInvalid hook --
        // clear the stale global session here so the next protected action
        // (in this panel or any other CJ modal) properly re-prompts instead
        // of silently reusing a token the server already rejected.
        if (isCJConfirmationError(data.code)) clearCJConfirmation();
        setPasswordError(data.error);
        return;
      }
      setPasswordPrompt(null);
      onSynced?.();
    } catch (error) {
      if (isCJConfirmationError(error?.code)) clearCJConfirmation();
      setPasswordError(error?.message || "CJ synchronization failed.");
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  const syncedIds = new Set(
    syncedFilterProducts.map((entry) => entry?.filter?.__cjVariantId).filter(Boolean)
  );
  const unsyncedVariants = (cjVariants || []).filter(
    (variant) => variant?.externalVariantId && !syncedIds.has(variant.externalVariantId)
  );
  const totalKnown = unsyncedVariants.length + syncedFilterProducts.length;
  const allSynced = totalKnown > 0 && unsyncedVariants.length === 0;

  const t = {
    title: elakayAutoText("h_cjvariants_title", "CJ Dropshipping Variants"),
    availableTitle: elakayAutoText("h_cjvariants_available_title", "Available"),
    syncedTitle: elakayAutoText("h_cjvariants_synced_title", "Synced to this product"),
    syncAll: elakayAutoText("h_cjvariants_sync_all", "Sync All"),
    syncing: elakayAutoText("h_cjvariants_syncing", "Syncing..."),
    empty: elakayAutoText("h_cjvariants_empty", "No variants yet"),
    allSynced: elakayAutoText("h_cjvariants_all_synced", "All CJ variants are synced"),
    progress: elakayAutoText("h_cjvariants_progress", "{synced} of {total} variants synced"),
    addOne: elakayAutoText("h_cjvariants_add_one", "Add to product"),
    deleteOne: elakayAutoText("h_cjvariants_delete_one", "Remove from product"),
    displayStyle: elakayAutoText("h_cjvariants_display_style", "Storefront display style"),
    styleSquare: elakayAutoText("h_cjvariants_style_square", "Square"),
    styleCircle: elakayAutoText("h_cjvariants_style_circle", "Circle"),
    styleImages: elakayAutoText("h_cjvariants_style_images", "Images"),
    styleColorCircle: elakayAutoText("h_cjvariants_style_color_circle", "Color Circle"),
    styleColorSquare: elakayAutoText("h_cjvariants_style_color_square", "Color Square"),
    styleProduct: elakayAutoText("h_cjvariants_style_product", "Product Card"),
    styleNone: elakayAutoText("h_cjvariants_style_none", "Hidden"),
  };

  const SWATCH_STYLE_OPTIONS = [
    { value: "square", label: t.styleSquare, preview: <span className="style-shape style-shape--square" /> },
    { value: "circle", label: t.styleCircle, preview: <span className="style-shape style-shape--circle" /> },
    { value: "images", label: t.styleImages, preview: <MdImage /> },
    { value: "colorCircle", label: t.styleColorCircle, preview: <span className="style-shape style-shape--circle style-shape--color" /> },
    { value: "colorSquare", label: t.styleColorSquare, preview: <span className="style-shape style-shape--square style-shape--color" /> },
    { value: "product", label: t.styleProduct, preview: <MdViewModule /> },
    { value: "none", label: t.styleNone, preview: <MdVisibilityOff /> },
  ];

  const handleAddOne = (variant) => () => {
    if (pendingId) return;
    openPasswordPrompt((confirmationToken) => {
      setPendingId(variant.externalVariantId);
      return convertCJVariantsToFilterProduct(token, productsID, null, null, variant.externalVariantId, { confirmationToken, confirmationAction: PASSWORD_CONFIRMATION_ACTIONS.CJ_FILTER_UPDATE })
        .finally(() => setPendingId(null));
    }, { mode: "confirm", title: `Synchronize variant “${variant.name || variant.sku}”?`, body: "This variant will be added to the Elakay product." });
  };

  const handleSyncAll = () => {
    if (syncingAll || !unsyncedVariants.length) return;
    openPasswordPrompt((confirmationToken) => {
      setSyncingAll(true);
      return convertCJVariantsToFilterProduct(token, productsID, null, null, undefined, { confirmationToken, confirmationAction: PASSWORD_CONFIRMATION_ACTIONS.CJ_FILTER_UPDATE })
        .finally(() => setSyncingAll(false));
    }, { mode: "sync-all", title: elakayAutoText("h_31aa1d4b6c214e", "Synchronize all CJ variants?"), body: `${unsyncedVariants.length} variant(s) will be added. Existing Elakay variants will be preserved.` });
  };

  const handleDeleteOne = (entry) => () => {
    if (pendingId) return;
    openPasswordPrompt((confirmationToken) => {
      setPendingId(entry._id);
      return deleteFilterProduct(token, productsID, entry._id, false, { confirmationToken, confirmationAction: PASSWORD_CONFIRMATION_ACTIONS.CJ_FILTER_DELETE })
        .finally(() => setPendingId(null));
    }, { mode: "delete-filter", title: elakayAutoText("h_a041212134710b", "Remove synchronized variant?"), body: `The linked CJ variant “${entry.filter?.__cjSku || entry.filter?.style || entry.filter?.color || "Unknown"}” will be removed from this product. The CJ source product is not deleted.` });
  };

  const handleStyleChange = (style) => () => {
    if (updatingStyle || style === variantSwatchStyle) return;
    setUpdatingStyle(true);
    updateCJVariantSwatchStyle(token, productsID, style)
      .then(() => onSynced?.())
      .finally(() => setUpdatingStyle(false));
  };

  return (
    <div className="cjvariants-panel">
      <div className="product-header-modern cjvariants-header">
        <h3 className="product-title-modern">{t.title}</h3>
        {totalKnown > 0 && (
          <div className={`cjvariants-progress${allSynced ? " cjvariants-progress--done" : ""}`}>
            <div className="cjvariants-progress__bar">
              <div
                className="cjvariants-progress__fill"
                style={{ width: `${Math.round((syncedFilterProducts.length / totalKnown) * 100)}%` }}
              />
            </div>
            <span className="cjvariants-progress__label">
              {allSynced ? (
                <><MdCheckCircle className="cjvariants-progress__icon" /> {t.allSynced}</>
              ) : (
                t.progress
                  .replace("{synced}", syncedFilterProducts.length)
                  .replace("{total}", totalKnown)
              )}
            </span>
          </div>
        )}
      </div>

      <div className="cjvariants-style-row">
        <span className="cjvariants-style-label">{t.displayStyle}</span>
        <div className="style-picker-grid">
          {SWATCH_STYLE_OPTIONS.map((opt) => {
            const active = variantSwatchStyle === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={`style-picker-card ${active ? "active" : ""}`}
                disabled={updatingStyle}
                onClick={handleStyleChange(opt.value)}
              >
                {active && <MdCheck className="style-picker-check" />}
                <span className="style-picker-preview">{opt.preview}</span>
                <span className="style-picker-label">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="product-content-modern">
        <div className="filters-grid-modern">
          {/* Available CJ variants (left) */}
          <div className="filter-values-section">
            <div className="section-header">
              <h4 className="section-title">
                {t.availableTitle}{unsyncedVariants.length > 0 ? ` (${unsyncedVariants.length})` : ""}
              </h4>
              {unsyncedVariants.length > 0 && (
                <button
                  type="button"
                  className="btn-sync-all"
                  disabled={syncingAll}
                  onClick={handleSyncAll}
                >
                  <MdSync className={syncingAll ? "icon-spin" : ""} />
                  {syncingAll ? t.syncing : t.syncAll}
                </button>
              )}
            </div>

            <div className="filters-list-container">
              {unsyncedVariants.length === 0 && (
                <div className="cjvariants-empty">
                  <MdCheckCircle className="cjvariants-empty__icon cjvariants-empty__icon--success" />
                  {t.allSynced}
                </div>
              )}
              {unsyncedVariants.map((variant) => (
                <div key={variant.externalVariantId} className="filter-item cjvariants-item">
                  <div className="cjvariants-thumb">
                    <ShowimagesLoading src={variant.image} alt={variant.name} />
                  </div>
                  <div className="filter-item-content">
                    <span className="filter-name">{variant.name}</span>
                    <span className="cjvariants-meta">
                      {variant.sku} · {variant.stockQuantity ?? 0}{elakayAutoText("h_7a40c832f35377", "in stock · $")}{Number(variant.suggestedSalePrice || 0).toFixed(2)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="cjvariants-add-btn"
                    title={t.addOne}
                    aria-label={t.addOne}
                    disabled={pendingId === variant.externalVariantId}
                    onClick={handleAddOne(variant)}
                  >
                    {pendingId === variant.externalVariantId
                      ? <span className="cjvariants-spinner" />
                      : <MdAdd />}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Synced variants (right) */}
          <div className="selected-filters-section">
            <div className="section-header">
              <h4 className="section-title">
                {t.syncedTitle}{syncedFilterProducts.length > 0 ? ` (${syncedFilterProducts.length})` : ""}
              </h4>
            </div>

            <div className="filters-list-container">
              {syncedFilterProducts.length === 0 && (
                <div className="cjvariants-empty">
                  <MdInventory2 className="cjvariants-empty__icon" />
                  {t.empty}
                </div>
              )}
              {syncedFilterProducts.map((entry) => (
                <div key={entry._id} className="filter-item cjvariants-item cjvariants-item--synced">
                  <MdCheckCircle className="cjvariants-synced-check" />
                  <div className="cjvariants-thumb">
                    <ShowimagesLoading src={entry.filter?.__cjImage} alt={entry.filter?.__cjSku} />
                  </div>
                  <div className="filter-item-content">
                    <span className="filter-name">{entry.filter?.__cjSku || entry.filter?.style || entry.filter?.color}</span>
                    <span className="cjvariants-meta">
                      {entry.quantity ?? 0}{elakayAutoText("h_59aa9f0204fbfa", "in stock")}</span>
                  </div>
                  <button
                    type="button"
                    className="filter-item-delete"
                    title={t.deleteOne}
                    aria-label={t.deleteOne}
                    disabled={pendingId === entry._id}
                    onClick={handleDeleteOne(entry)}
                  >
                    {pendingId === entry._id
                      ? <span className="cjvariants-spinner" />
                      : <MdDeleteForever />}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <CJSyncActionModal
        show={Boolean(passwordPrompt)}
        onHide={closePasswordPrompt}
        mode={passwordPrompt?.mode || "confirm"}
        title={passwordPrompt?.title || elakayAutoText("h_cjvariants_password_title", "Confirm CJ synchronization")}
        loading={isSubmittingPassword}
        error={passwordError}
        passwordRequired
        confirmationAction={passwordPrompt?.mode === "delete-filter" ? PASSWORD_CONFIRMATION_ACTIONS.CJ_FILTER_DELETE : PASSWORD_CONFIRMATION_ACTIONS.CJ_FILTER_UPDATE}
        onPasswordConfirm={({ password, action }) => requestElakayPasswordConfirmation({ baseUrl: elakay_API, token, password, action })}
        passwordLabel={elakayAutoText("h_cjvariants_password_label", "Mot de passe Elakay")}
        confirmLabel={elakayAutoText("h_cjvariants_password_confirm", "Confirmer")}
        cancelLabel={elakayAutoText("h_cjvariants_password_cancel", "Annuler")}
        onConfirm={submitPasswordPrompt}
      >
        {passwordPrompt?.body && <p>{passwordPrompt.body}</p>}
      </CJSyncActionModal>
    </div>
  );
};

export default CJVariantsSyncPanel;
